import { useCallback, useEffect, useRef, useState } from 'react';

export function useClickMenu(onClose?: () => void) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Dismiss on `click` rather than `pointerdown`: browsers fire pointerdown on <html> when
    // the page scrollbar is pressed, but never a click, so scrolling keeps the menu open.
    // Tracking where the press started stops a drag from inside the menu closing it.
    let pressedInside = false;
    const isInside = (target: EventTarget | null) => !!ref.current?.contains(target as Node);

    const onPointerDown = (event: PointerEvent) => {
      pressedInside = isInside(event.target);
    };
    const onClick = (event: MouseEvent) => {
      if (pressedInside || isInside(event.target)) return;
      setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  return { ref, isOpen, toggle: () => setIsOpen((open) => !open), dismiss };
}
